import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  buildPartnerGroupChannelMessageMapOptions,
  extractPartnerGroupChannelApiMessage,
  mapPartnerGroupChannelChatMessage,
} from '../components/Partner/Community/mapPartnerGroupChannel';
import { updatePartnerGroupChatMessageInCache } from '../components/Partner/Community/partnerGroupChannelMessagesCache';
import { updatePartnerGroupChannelMessage } from '../services/partnerGroupChannel';
import { partnerGroupChannelQueryKeys } from './partner-group-channel-query-keys';

export const useUpdatePartnerGroupChatMessageMutation = (options = {}) => {
  const queryClient = useQueryClient();
  const { firebaseUid, partnerProfile } = useAuth();
  const messageMapOptions = useMemo(
    () => buildPartnerGroupChannelMessageMapOptions({ firebaseUid, partnerProfile }),
    [firebaseUid, partnerProfile],
  );
  const { onSuccess, onError, ...restOptions } = options;

  return useMutation({
    mutationFn: async ({ messageId, messageText, channelId } = {}) => {
      const result = await updatePartnerGroupChannelMessage(messageId, {
        messageText,
        channelId,
      });
      if (!result.success) {
        throw new Error(result.message || 'Failed to update message');
      }
      return { messageId, result };
    },
    onSuccess: async (payload, variables, context) => {
      const raw = extractPartnerGroupChannelApiMessage(payload.result.data);
      const mapped = raw
        ? mapPartnerGroupChannelChatMessage(raw, 0, messageMapOptions)
        : {
            id: variables.messageId,
            text: String(variables.messageText ?? '').trim(),
            editedAt: new Date().toISOString(),
          };

      if (mapped?.id) {
        updatePartnerGroupChatMessageInCache(queryClient, mapped.id, (message) => ({
          ...message,
          ...mapped,
          text: mapped.text || String(variables.messageText ?? '').trim(),
        }));
      } else {
        await queryClient.invalidateQueries({
          queryKey: [...partnerGroupChannelQueryKeys.all, 'messages'],
        });
      }

      await onSuccess?.(payload, variables, context);
    },
    onError: (error, variables, context) => {
      toast.error(error?.message || 'Failed to update message');
      onError?.(error, variables, context);
    },
    ...restOptions,
  });
};

export default useUpdatePartnerGroupChatMessageMutation;
